import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { DateTime } from 'luxon';
import { SchoolOperative } from '../../../db/dataTransferObjects/SchoolOperative.js';
import { Role } from '../../../db/semantics/Role.js';
import { Taetigkeit } from '../../../db/semantics/Taetigkeit.js';
import type { ChancenwerkUser } from '../../users/entities/ChancenwerkUser.js';
import { KooperationsPartner } from './KooperationsPartner.js';
import { Partner } from './Partner.js';

export enum Schulform {
  GEMEINSCHAFTSGRUNDSCHULE = 'Gemeinschaftsgrundschule',
  GEMEINSCHAFTSHAUPTSCHULE = 'Gemeinschaftshauptschule',
  GEMEINSCHAFTSSCHULE = 'Gemeinschaftsschule',
  GESAMTSCHULE = 'Gesamtschule',
  GRUNDSCHULE = 'Grundschule',
  GYMNASIUM = 'Gymnasium',
  HAUPT = 'Haupt- & Realschule',
  MITTELSCHULE = 'Mittelschule',
  OBERSCHULE = 'Oberschule',
  REALSCHULE = 'Realschule',
  REALSCHULE_PLUS = 'Realschule plus',
  SEKUNDARSCHULE = 'Sekundarschule',
  STADTTEILSCHULE = 'Stadtteilschule',
  GRUND_UND_REALSCHLE_PLUS = 'Grund- und Realschule plus',
  GRUND_UND_WERKREAL = 'Grund- und Werkrealschule',
  BERUFSSCHULE = 'Berufsschule',
}

export enum Standort {
  EIGENER_STANDORT = 'Eigener Standort',
  SCHULSTANDORT = 'Schulstandort',
}

/**
 * @entity ZugConfiguration
 */
@Entity()
export class ZugConfiguration extends AdornisEntity {
  public static override _class = 'ZugConfiguration';

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(type => String)
  public jahrgangsstufe: Maybe<string>;

  @Field(type => String)
  public zugAvailable: Maybe<string>;
}

/**
 * @entity School
 */
@Entity()
export class School extends Partner {
  public static override _class = 'School';

  @validate<School>(({ value, key, source }) => {
    if (!!value && !(Object.values(Schulform) as string[]).includes(value))
      throw new ValidationError(`${value} ist keine gültige Schulform.`, { key, source });
  })
  @Field(type => String)
  public schulform?: Schulform;

  @Field(type => String)
  public hasBuTStatus: Maybe<string>;

  @Field(type => DateTime)
  public lastLearningMaterialOrderDate?: DateTime;

  @Field(type => DateTime)
  public lastAdvertisingMaterialOrderDate?: DateTime;

  @Field(type => String)
  public materialClosetOwner: Maybe<string>;

  @Field(type => String)
  public materialClosetPosition: Maybe<string>;

  @Field(type => Boolean)
  public materialClosetOwnerIsCW: Maybe<boolean>;

  @Field(type => String)
  public keyLernfoerderungRooms: Maybe<string>;

  @Field(type => Boolean)
  public personalAllowedToUsePrinter: Maybe<boolean>;

  @Field(type => [ZugConfiguration])
  public zugConfiguration: Maybe<ZugConfiguration[]>;

  @Field(type => String)
  public mediaID: Maybe<string>;

  @DataLoadedField(type => ImageFile, ({ mediaID }) => mediaID)
  public media: Maybe<ImageFile>;

  @Field(type => Boolean, {
    default: v => v ?? false,
  })
  public hideOnWebsite?: boolean;

  @Field(type => SchoolOperative, {
    resolve: function (this: School) {
      return async () => {
        const kpfCollection = await getCollection<KPF>(KPF._class);
        let kpfs = await kpfCollection.find({ schoolID: this._id }).toArray();

        if (!kpfs) {
          return null;
        }

        // ! Prioritize ProductLKK as "source of truth"
        kpfs = kpfs.sort(k => {
          return k.produkt?._class === ProductLKK._class ? -1 : 1;
        });

        const userCollection = await getRawCollection<EntityData<ChancenwerkUser>>(AdornisUser._collectionName);

        for (let i = 0; i < kpfs.length; i++) {
          const relationshipCollection = await getRawCollection(Taetigkeit._collectionName);
          const relationships = await relationshipCollection
            .find({
              predicateID: Role.INTERNAL_KPF_OPERATIV,
              objectID: kpfs[i]?._id,
            })
            .toArray();
          const taetigkeiten = relationships
            .map(user => constructValue(user) as Taetigkeit)
            .filter(t => (t.endDate ? t.endDate.toUnixInteger() > DateTime.now().toUnixInteger() : true));
          for (const taetigkeit of taetigkeiten) {
            if (!taetigkeit.subjectID) {
              continue;
            }

            const mitarbeiter = await userCollection.findOne(
              { _id: taetigkeit.subjectID },
              {
                projection: {
                  _id: 1,
                  vorname: 1,
                  nachname: 1,
                  email: 1,
                },
              },
            );

            if (!mitarbeiter) {
              continue;
            }

            return new SchoolOperative({
              name: `${mitarbeiter.vorname} ${mitarbeiter.nachname}`,
              email: mitarbeiter.email,
            });
          }
        }

        return null;
      };
    },
  })
  public operative?: SchoolOperative;

  @Field(type => String)
  public wlanAccess: Maybe<string>;

  @Field(type => String)
  public wlanPassword: Maybe<string>;

  @Field(type => String)
  public noWlanReason: Maybe<string>;

  @Query(type => [School])
  public static getAllSchools() {
    return async (gqlFields: BaseQLSelectionSet<School>) => {
      const collection = await getCollection<any>(School._class);
      const schools = await collection.find({ _class: School._class }).toArray();
      return schools.map(s => constructValue(s) as School);
    };
  }

  @Query(type => [School])
  public static getAllWithoutIDs(@Arg('ids', type => [String]) ids: string[]) {
    return async (gqlFields: BaseQLSelectionSet<School>) => {
      const collection = await getCollection<any>(School._class);
      const schools = await collection.find({ _class: School._class, _id: { $nin: ids as any } }).toArray();
      return schools.map(s => constructValue(s) as School);
    };
  }

  public override get type() {
    return KooperationsPartner.SCHULE;
  }
}

if (A.isServer) {
  const schools = getRawCollection<any>(School._collectionName);

  // Create DB Indices
  void schools.then(async db => {
    await db.createIndex({ _id: 1 });
    await db.createIndex({ kuerzel: 1 });
    await db.createIndex({ bundesland: 1 });
    await db.createIndex({ 'zipCity.city': 1, 'zipCity.zip': 1 });
  });
}
